import {
  ArrowRightLeft,
  Gauge,
  ListChecks,
  Activity,
  ArrowLeftRight,
  BarChart3,
  Building2,
  Calendar,
  ClipboardCheck,
  ClipboardList,
  Eye,
  FileBarChart,
  Globe,
  HandCoins,
  History,
  Layers,
  Landmark,
  LayoutDashboard,
  MessageSquare,
  Package,
  Receipt,
  ReceiptText,
  Scale,
  ShieldCheck,
  ShoppingBasket,
  ShoppingCart,
  SlidersHorizontal,
  Stethoscope,
  Store,
  Tags,
  TrendingUp,
  Truck,
  UserCog,
  Users,
  Users2,
  Wallet,
  Warehouse,
  Pill,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type { FeatureKey, MeResponse } from "@/features/auth/types";
import { can, hasFeature, showsActs, showsOptical } from "@/lib/permissions/can";

export interface NavItem {
  label: string;
  href: string;
  permission?: string;
  icon?: LucideIcon;
  /** Masqué si la clinique active ne suit pas le stock. */
  requiresStock?: boolean;
  /** Réservé aux cliniques de spécialité « optique » (modules ophtalmo). */
  requiresOptical?: boolean;
  /** Réservé aux cliniques qui vendent des actes (ADR 0047, `showsActs`). */
  requiresActs?: boolean;
  /** Réservé au personnel éditeur GoldenTech (is_platform_staff). */
  platformStaff?: boolean;
  /** Réservé à l'administration de la configuration : super administrateur de
   *  l'abonné ou éditeur GTS (cf. `canManageAccountSettings`). */
  accountSettings?: boolean;
  /** @deprecated conservé pour les configurations d'affichage enregistrées. */
  /** Plan éditeur : staff plateforme OU superuser technique (aligné sur le
   *  backend `IsSuperUserOrSafeAccessible` — gestion des cliniques). */
  editorPlan?: boolean;
  /** Niveau console minimal (1 = tout staff, 2 = gérant+, 3 = super). */
  minPlatformLevel?: number;
  /** Hub de configuration : superuser technique OU éditeur GTS niveau super. */
  configHub?: boolean;
  /** Nouveauté à activer pour la clinique (ADR 0036) : masqué sinon. */
  feature?: FeatureKey;
  /** Seconde nouveauté exigée en plus de `feature` (bons de transfert, ADR 0060). */
  alsoFeature?: FeatureKey;
  /** Sous-titre du menu sous lequel l'entrée se range (ADR 0061). Les
   *  entrées consécutives de même sous-titre forment un bloc. */
  section?: string;
  /** Menu de la configuration d'affichage (`hidden_nav`) qui masque aussi
   *  cette entrée. Défaut : le chemin de `href`, sans requête ni ancre. */
  hiddenBy?: string;
  /** Quand ce module est actif, l'entrée mène ailleurs sous un autre libellé
   *  (espace Articles, ADR 0062). Mêmes conditions de visibilité. */
  swapWhen?: { feature: FeatureKey; label: string; href: string };
  /** Ligne d'aide affichée sous l'entrée dans le menu (ADR 0069). */
  hint?: string;
  /** Pages voisines ouvertes en onglets (ADR 0069) : chacune garde ses
   *  conditions ; l'entrée est visible si un onglet l'est, et mène au premier
   *  onglet visible. */
  tabs?: NavItem[];
  /** Lien de la barre réduit à son icône (Paramètres, ADR 0069). */
  iconOnly?: boolean;
}

/** Rangs des 3 niveaux du personnel éditeur (User.platform_role). */
const PLATFORM_LEVELS: Record<string, number> = {
  account_manager: 1,
  manager: 2,
  super: 3,
};

/** Rang console d'un utilisateur (0 = pas staff plateforme). Filet pour un
 *  staff sans niveau : superuser → super, sinon moindre privilège. */
export function platformLevel(
  me:
    | {
        is_platform_staff?: boolean;
        platform_role?: string | null;
        is_superuser?: boolean;
      }
    | null
    | undefined,
): number {
  if (!me?.is_platform_staff) return 0;
  const level = me.platform_role
    ? PLATFORM_LEVELS[me.platform_role]
    : undefined;
  if (level) return level;
  return me.is_superuser ? 3 : 1;
}

/** Accès aux paramètres du compte (`/parametres`) : superuser technique,
 *  éditeur GTS, ou rôle métier `super_admin` de l'abonné. Le propriétaire de
 *  compte en est exclu — miroir exact de `_is_config_admin` côté backend. */
export function canManageAccountSettings(
  me: MeResponse | null | undefined,
): boolean {
  if (!me) return false;
  if (me.is_superuser || me.is_platform_staff) return true;
  // `active_roles` peut manquer d'un `me` mis en cache par une version
  // antérieure (hors ligne) : on refuse alors l'accès plutôt que de casser.
  return (me.active_roles ?? []).some((r) => r.code === "super_admin");
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    label: "Pilotage",
    items: [
      { label: "Tableau de bord", href: "/dashboard", icon: LayoutDashboard },
      {
        label: "Statistiques",
        href: "/statistiques",
        permission: "reporting.view",
        icon: TrendingUp,
      },
    ],
  },
  {
    label: "Clinique",
    items: [
      {
        label: "Patients",
        href: "/patients",
        permission: "patients.view",
        icon: Users,
      },
      {
        label: "Clients",
        href: "/clients",
        permission: "clients.view",
        icon: UserCog,
      },
      {
        label: "Familles",
        href: "/familles",
        permission: "patients.family.view",
        icon: Users2,
      },
      {
        label: "Rendez-vous",
        href: "/rendez-vous",
        permission: "appointments.view",
        icon: Calendar,
      },
      {
        label: "Consultations",
        href: "/consultations",
        permission: "consultations.view",
        icon: Stethoscope,
      },
      {
        label: "Examens",
        href: "/examens",
        permission: "examinations.view",
        icon: Activity,
      },
      {
        label: "Prescriptions",
        href: "/prescriptions",
        permission: "prescriptions.view",
        icon: ClipboardList,
      },
      {
        label: "Prescriptions optiques",
        href: "/eye-prescriptions",
        permission: "eye_prescriptions.view",
        icon: Eye,
        requiresOptical: true,
      },
    ],
  },
  {
    label: "Gestion",
    items: [
      {
        label: "Actes et tarifs",
        href: "/actes",
        permission: "inventory.view",
        icon: Stethoscope,
        requiresActs: true,
      },
      {
        label: "Produits",
        href: "/catalogue",
        permission: "inventory.view",
        icon: Package,
        swapWhen: { feature: "catalog_plus", label: "Articles", href: "/articles" },
      },
      {
        label: "Catégories",
        href: "/categories",
        permission: "inventory.view",
        icon: Tags,
        swapWhen: { feature: "catalog_plus", label: "Catégories", href: "/articles?section=categories" },
      },
      {
        label: "Stock",
        href: "/stock",
        permission: "inventory.view",
        icon: ClipboardCheck,
        requiresStock: true,
      },
      // Module « Stock et inventaire avancés » (ADR 0060).
      {
        label: "Inventaires",
        href: "/stock/inventaires",
        permission: "inventory.view",
        icon: ListChecks,
        requiresStock: true,
        feature: "stock_plus",
      },
      {
        label: "Transferts",
        href: "/stock/transferts",
        permission: "inventory.view",
        icon: ArrowRightLeft,
        requiresStock: true,
        feature: "stock_plus",
        alsoFeature: "stock_per_clinic",
      },
      {
        label: "Seuils de stock",
        href: "/stock/seuils",
        permission: "inventory.view",
        icon: Gauge,
        requiresStock: true,
        feature: "stock_plus",
      },
      // Module « Contrôle du stock » (ADR 0070) : stock comparé à son journal.
      {
        label: "Contrôle du stock",
        href: "/stock/controle",
        permission: "inventory.view",
        icon: ShieldCheck,
        requiresStock: true,
        feature: "stock_control",
      },
      // Option « Achats et fournisseurs » (ADR 0054).
      {
        label: "Achats",
        href: "/achats",
        permission: "purchasing.view",
        icon: Truck,
        requiresStock: true,
        feature: "purchasing",
      },
      {
        label: "Fournisseurs",
        href: "/fournisseurs",
        permission: "purchasing.view",
        icon: Building2,
        feature: "purchasing",
      },
      {
        label: "Proformas",
        href: "/proformas",
        permission: "proformas.view",
        icon: ClipboardList,
      },
      {
        label: "Factures",
        href: "/factures",
        permission: "sales.view",
        icon: ShoppingCart,
      },
      {
        label: "Paiements",
        href: "/paiements",
        permission: "payments.view",
        icon: Receipt,
      },
      {
        label: "Clôture de caisse",
        href: "/caisse/cloture",
        permission: "cash_closure.view",
        icon: Wallet,
      },
      {
        label: "Trésorerie",
        href: "/tresorerie",
        permission: "treasury.view",
        icon: Landmark,
      },
      // Module « Dettes fournisseurs » (ADR 0056).
      {
        label: "Dettes fournisseurs",
        href: "/dettes-fournisseurs",
        permission: "payables.view",
        icon: HandCoins,
        feature: "supplier_payables",
      },
      // Module « Dépenses complètes » (ADR 0057).
      {
        label: "Dépenses",
        href: "/depenses",
        permission: "expenses.view",
        icon: ReceiptText,
        feature: "expenses_plus",
      },
      // Module « Résultat par agence » (ADR 0059).
      {
        label: "Résultat",
        href: "/resultat",
        permission: "reporting.profit",
        icon: Scale,
        feature: "profit_report",
      },
      {
        label: "Assurances",
        href: "/assurances",
        permission: "insurances.view",
        icon: ShieldCheck,
        feature: "insurance",
      },
      {
        label: "Rapport assurances",
        href: "/assurances/rapport",
        // Dissocié des rapports d'administration (`reporting.reports.view`) :
        // on peut avoir accès à l'un sans l'autre.
        permission: "reporting.insurance.view",
        icon: FileBarChart,
        feature: "insurance",
      },
      {
        label: "SMS",
        href: "/sms",
        permission: "sms.view",
        icon: MessageSquare,
      },
    ],
  },
  {
    label: "Administration",
    items: [
      // Gestion des cliniques = plan ÉDITEUR (staff GTS ou superuser technique).
      {
        label: "Cliniques",
        href: "/admin/organisations",
        icon: Building2,
        editorPlan: true,
      },
      // Module « Boutiques — points de vente » (ADR 0064) : masqué sans lui.
      {
        label: "Boutiques",
        href: "/admin/boutique",
        permission: "boutiques.view",
        icon: Store,
        feature: "boutiques",
      },
      {
        label: "Utilisateurs",
        href: "/admin/utilisateurs",
        permission: "rbac.role.assign",
        icon: UserCog,
      },
      {
        label: "Rôles",
        href: "/admin/roles",
        permission: "rbac.role.manage",
        icon: ShieldCheck,
      },
      {
        label: "Types d'examen",
        href: "/admin/types-examen",
        permission: "examinations.update",
        icon: SlidersHorizontal,
      },
      {
        label: "Médicaments",
        href: "/admin/medicaments",
        permission: "prescriptions.view",
        icon: Pill,
        feature: "medication_catalog",
      },
      {
        label: "Audit",
        href: "/admin/audit",
        permission: "audit.view",
        icon: History,
      },
      {
        label: "Rapports",
        href: "/admin/rapports",
        permission: "reporting.reports.view",
        icon: BarChart3,
      },
      // « Abonnement » (/abonnement, droit `billing.subscription.pay`) : entrée
      // volontairement absente du menu tant que GoldenTech n'a pas validé les
      // prix affichés aux abonnés en place. La page reste servie par sa route.
    ],
  },
  {
    // Menu top-level dédié — hub de configuration (super admin / éditeur GTS).
    // Groupe à item unique → rendu comme lien direct dans la navbar.
    label: "Paramètres",
    items: [
      {
        label: "Paramètres",
        href: "/configuration",
        icon: SlidersHorizontal,
        configHub: true,
      },
    ],
  },
];

/** Entrée de `NAV_GROUPS` reprise telle quelle (mêmes droits, mêmes
 *  conditions), avec un libellé ou un sous-titre propre au nouveau rangement. */
function fromLegacy(href: string, extra: Partial<NavItem> = {}): NavItem {
  const item = NAV_GROUPS.flatMap((g) => g.items).find((i) => i.href === href);
  if (!item) throw new Error(`navigation: unknown legacy entry ${href}`);
  return { ...item, ...extra };
}

function legacyGroup(label: string): NavGroup {
  const group = NAV_GROUPS.find((g) => g.label === label);
  if (!group) throw new Error(`navigation: unknown legacy group ${label}`);
  return group;
}

const CATALOGUE = "Catalogue";
const STOCK = "Stock";
const SUPPLY = "Approvisionnement";

/**
 * Nouvelle organisation des menus (module `navigation_v2`, ADR 0061).
 *
 * Le menu « Gestion » se répartit en Ventes, Stock, Finance et Communication.
 * Chaque entrée existante garde ses conditions (reprises de `NAV_GROUPS`) ;
 * les entrées ajoutées ne sont que des liens profonds vers des onglets
 * existants, ou la page d'accueil du stock. Aucune route n'est retirée.
 * Module éteint : `NAV_GROUPS`, inchangé.
 */
export const NAV_V2_GROUPS: NavGroup[] = [
  legacyGroup("Pilotage"),
  legacyGroup("Clinique"),
  {
    label: "Ventes",
    items: [
      fromLegacy("/proformas"),
      fromLegacy("/factures"),
      fromLegacy("/paiements"),
      fromLegacy("/actes"),
      fromLegacy("/assurances"),
      fromLegacy("/assurances/rapport"),
    ],
  },
  {
    label: "Stock",
    items: [
      {
        label: "Tableau de bord stock",
        href: "/stock/accueil",
        permission: "inventory.view",
        icon: Warehouse,
        requiresStock: true,
        feature: "navigation_v2",
        hiddenBy: "/stock",
      },
      fromLegacy("/catalogue", { section: CATALOGUE }),
      {
        // Registre des variantes : onglet de la page Produits (ADR 0058).
        label: "Variantes",
        href: "/catalogue?vue=variantes",
        permission: "inventory.view",
        icon: Layers,
        feature: "catalog_plus",
        alsoFeature: "product_variants",
        section: CATALOGUE,
        swapWhen: { feature: "catalog_plus", label: "Variantes", href: "/articles?section=variantes" },
      },
      fromLegacy("/categories", { section: CATALOGUE }),
      fromLegacy("/stock", { label: "Niveaux de stock", section: STOCK }),
      {
        // Journal des mouvements : bloc de la page Stock.
        label: "Mouvements",
        href: "/stock?vue=mouvements",
        permission: "inventory.view",
        icon: ArrowLeftRight,
        requiresStock: true,
        section: STOCK,
      },
      // Stock et inventaire avancés (ADR 0060).
      fromLegacy("/stock/inventaires", { section: STOCK }),
      fromLegacy("/stock/transferts", { section: STOCK }),
      fromLegacy("/stock/seuils", { section: STOCK }),
      fromLegacy("/stock/controle", { section: STOCK }),
      // Onglet « À réapprovisionner » de la page Achats (ADR 0054).
      fromLegacy("/achats", {
        label: "Réapprovisionnement",
        href: "/achats?tab=reorder",
        icon: ShoppingBasket,
        section: SUPPLY,
      }),
      fromLegacy("/achats", { label: "Achats / réceptions", section: SUPPLY }),
      fromLegacy("/fournisseurs", { section: SUPPLY }),
    ],
  },
  {
    label: "Finance",
    items: [
      fromLegacy("/caisse/cloture"),
      fromLegacy("/tresorerie"),
      fromLegacy("/depenses"),
      fromLegacy("/dettes-fournisseurs"),
      fromLegacy("/resultat"),
    ],
  },
  {
    label: "Communication",
    items: [fromLegacy("/sms")],
  },
  legacyGroup("Administration"),
  legacyGroup("Paramètres"),
];

/** Entrée de `NAV_V2_GROUPS` (liens profonds, tableau de bord du stock). */
function fromV2(href: string, extra: Partial<NavItem> = {}): NavItem {
  const item = NAV_V2_GROUPS.flatMap((g) => g.items).find((i) => i.href === href);
  if (!item) throw new Error(`navigation: unknown v2 entry ${href}`);
  return { ...item, ...extra };
}

/** Entrée qui regroupe des pages voisines en onglets (ADR 0069). */
function tabbed(label: string, tabs: NavItem[], extra: Partial<NavItem> = {}): NavItem {
  const first = tabs[0];
  if (!first) throw new Error(`navigation: empty tabbed entry ${label}`);
  // Le sous-titre et l'aide sont ceux de l'entrée, pas des onglets.
  const pages = tabs.map((t) => ({ ...t, section: undefined, hint: undefined }));
  return { label, href: first.href, icon: first.icon, tabs: pages, ...extra };
}

const ACCUEIL = "Accueil";
const MEDICAL = "Médical";
const CAISSE = "Caisse";
const CHARGES = "Charges";

/**
 * Menus simplifiés (module `navigation_v3`, ADR 0069) : moins de menus, mêmes
 * noms. Chaque page garde son libellé, ses droits et son adresse ; les pages
 * voisines s'ouvrent en onglets (`tabs`). Prime sur `navigation_v2`.
 */
export const NAV_V3_GROUPS: NavGroup[] = [
  {
    label: "Tableau de bord",
    items: [fromLegacy("/dashboard")],
  },
  {
    label: "Clinique",
    items: [
      tabbed("Patients", [fromLegacy("/patients"), fromLegacy("/familles")], {
        section: ACCUEIL,
        hint: "Fiches, familles, historique et documents",
      }),
      fromLegacy("/rendez-vous", { section: ACCUEIL, hint: "Agenda du jour et rappels" }),
      fromLegacy("/sms", { section: ACCUEIL, hint: "Rappels, campagnes et crédit SMS" }),
      fromLegacy("/consultations", { section: MEDICAL, hint: "Examen du patient et compte rendu" }),
      fromLegacy("/examens", { section: MEDICAL, hint: "Résultats d'examens complémentaires" }),
      // Clinique d'optique : la page s'ouvre sur les prescriptions optiques.
      tabbed("Prescriptions", [fromLegacy("/eye-prescriptions"), fromLegacy("/prescriptions")], {
        section: MEDICAL,
        hint: "Ordonnances de médicaments, de verres et de lentilles",
      }),
    ],
  },
  {
    label: "Ventes",
    items: [
      fromLegacy("/proformas", { hint: "Devis à remettre avant la facture" }),
      fromLegacy("/factures", { hint: "Ventes facturées et reste à payer" }),
      fromLegacy("/paiements", { hint: "Encaissements, reçus et remboursements" }),
      fromLegacy("/clients", { hint: "Acheteurs sans dossier patient" }),
      fromLegacy("/actes", { hint: "Prix des actes et des consultations" }),
      tabbed("Assurances", [fromLegacy("/assurances"), fromLegacy("/assurances/rapport")], {
        hint: "Part assurance, bordereaux, rapprochement et rapport",
      }),
    ],
  },
  {
    label: "Stock",
    items: [
      fromV2("/stock/accueil", { feature: "navigation_v3", hint: "Alertes, ruptures et valeur du stock" }),
      tabbed(
        "Produits",
        [fromLegacy("/catalogue"), fromV2("/catalogue?vue=variantes"), fromLegacy("/categories")],
        {
          hint: "Ce que la clinique vend, avec ses catégories",
          swapWhen: { feature: "catalog_plus", label: "Articles", href: "/articles" },
        },
      ),
      tabbed(
        "Niveaux de stock",
        [
          fromLegacy("/stock", { label: "Niveaux de stock" }),
          fromV2("/stock?vue=mouvements"),
          fromLegacy("/stock/seuils"),
          fromLegacy("/stock/controle"),
        ],
        { hint: "Quantités disponibles, entrées et sorties, seuils d'alerte" },
      ),
      fromLegacy("/stock/inventaires", { hint: "Comptages et écarts" }),
      fromLegacy("/stock/transferts", { hint: "Envois d'une agence à l'autre" }),
      // « À réapprovisionner » reste un onglet de la page Achats (ADR 0054).
      tabbed("Achats / réceptions", [fromLegacy("/achats", { label: "Achats / réceptions" }), fromLegacy("/fournisseurs")], {
        hint: "Articles à commander, commandes, réceptions et fournisseurs",
      }),
    ],
  },
  {
    label: "Finance",
    items: [
      fromLegacy("/caisse/cloture", { section: CAISSE, hint: "Fermer la caisse du jour" }),
      fromLegacy("/tresorerie", { section: CAISSE, hint: "Soldes des comptes, banque et mobile money" }),
      fromLegacy("/depenses", { section: CHARGES, hint: "Ce que la clinique paie" }),
      fromLegacy("/dettes-fournisseurs", { section: CHARGES, hint: "Ce que la clinique doit" }),
    ],
  },
  {
    label: "Rapports",
    items: [
      fromLegacy("/statistiques", { hint: "Activité et consultations en chiffres" }),
      fromLegacy("/resultat", { hint: "Bénéfice par agence" }),
      fromLegacy("/assurances/rapport", { hint: "Montants dus par chaque assureur" }),
      fromLegacy("/admin/rapports", { hint: "Rapports d'activité à imprimer" }),
    ],
  },
  {
    label: "Administration",
    items: [
      fromLegacy("/admin/organisations", { hint: "Agences du compte" }),
      fromLegacy("/admin/boutique", { hint: "Points de vente" }),
      tabbed("Utilisateurs", [fromLegacy("/admin/utilisateurs"), fromLegacy("/admin/roles")], {
        hint: "Comptes du personnel et droits de chaque fonction",
      }),
      fromLegacy("/admin/types-examen", { hint: "Liste proposée pendant la consultation" }),
      fromLegacy("/admin/medicaments", { hint: "Catalogue des médicaments" }),
      fromLegacy("/admin/audit", { hint: "Qui a fait quoi, et quand" }),
    ],
  },
  {
    label: "Paramètres",
    items: [fromLegacy("/configuration", { iconOnly: true })],
  },
];

/**
 * Navigation du personnel éditeur GoldenTech (espace ada / control plane).
 * Seule navigation visible pour le staff plateforme : l'éditeur gère les
 * abonnés / licences / plateforme et accède aux espaces des abonnés par
 * handoff (ClinicSwitcher), PAS par des menus cliniques. Groupes à item
 * unique = lien direct.
 */
export const PLATFORM_NAV_GROUPS: NavGroup[] = [
  {
    label: "Console",
    items: [
      {
        label: "Console",
        href: "/editeur",
        icon: LayoutDashboard,
        platformStaff: true,
      },
    ],
  },
  {
    label: "Abonnés",
    items: [
      {
        label: "Abonnés (espaces)",
        href: "/editeur/clients",
        icon: Building2,
        platformStaff: true,
      },
      {
        label: "Utilisateurs des espaces",
        href: "/editeur/utilisateurs",
        icon: Users,
        platformStaff: true,
      },
    ],
  },
  {
    label: "Licences",
    items: [
      {
        label: "Licences",
        href: "/editeur/licences",
        icon: Receipt,
        platformStaff: true,
      },
      {
        label: "Packs SMS",
        href: "/editeur/packs-sms",
        icon: MessageSquare,
        platformStaff: true,
      },
    ],
  },
  {
    // Administration de la plateforme — niveau « super » uniquement.
    label: "Plateforme",
    items: [
      {
        label: "Équipe GTS",
        href: "/editeur/equipe",
        icon: UserCog,
        platformStaff: true,
        minPlatformLevel: 3,
      },
      {
        label: "Domaines",
        href: "/editeur/domaines",
        icon: Globe,
        platformStaff: true,
        minPlatformLevel: 3,
      },
      {
        label: "Paramètres licences",
        href: "/editeur/parametres",
        icon: SlidersHorizontal,
        platformStaff: true,
        minPlatformLevel: 3,
      },
    ],
  },
  {
    // Menu top-level dédié — hub de configuration (éditeur GTS niveau super).
    // Groupe à item unique → rendu comme lien direct dans la navbar.
    label: "Paramètres",
    items: [
      {
        label: "Paramètres",
        href: "/configuration",
        icon: SlidersHorizontal,
        configHub: true,
      },
    ],
  },
];

/** Un item de navigation est-il visible pour cet utilisateur ? Regroupe toutes
 *  les règles de filtrage (permission, spécialité, plan éditeur, paramètres,
 *  niveau console, module masqué par la config d'affichage). `hiddenNav` = set
 *  des href masqués (issu de `me.active_display_config.hidden_nav`). */
export function isNavItemVisible(
  me: MeResponse | null | undefined,
  item: NavItem,
  hiddenNav: Set<string>,
): boolean {
  return (
    (!item.accountSettings || canManageAccountSettings(me)) &&
    (!item.permission || can(me ?? null, item.permission)) &&
    (!item.requiresStock || !!me?.active_stock_tracking) &&
    (!item.requiresOptical || showsOptical(me ?? null)) &&
    (!item.requiresActs || showsActs(me ?? null)) &&
    (!item.feature || hasFeature(me ?? null, item.feature)) &&
    (!item.alsoFeature || hasFeature(me ?? null, item.alsoFeature)) &&
    (!item.platformStaff || !!me?.is_platform_staff) &&
    (!item.editorPlan || !!me?.is_platform_staff || !!me?.is_superuser) &&
    (!item.minPlatformLevel || platformLevel(me) >= item.minPlatformLevel) &&
    // Configuration : éditeur GTS, ou permission `settings.manage` accordée
    // à un rôle (aucune par défaut — cf. `canSeeConfigHub`).
    (!item.configHub ||
      !!me?.is_superuser ||
      platformLevel(me) >= 3 ||
      can(me ?? null, "settings.manage")) &&
    !hiddenNav.has(navHideKey(item))
  );
}

/** Chemin d'un lien de menu, sans requête ni ancre (`/achats?tab=x` → `/achats`). */
export function hrefPath(href: string): string {
  return href.split(/[?#]/, 1)[0] ?? href;
}

/** Clé de masquage d'une entrée dans `hidden_nav` (cf. `NavItem.hiddenBy`). */
export function navHideKey(item: NavItem): string {
  return item.hiddenBy ?? hrefPath(item.href);
}

/** Page d'atterrissage après connexion / bascule. Le tableau de bord si visible
 *  (défaut) ; sinon le premier module accessible ; sinon le profil. Le staff
 *  éditeur va toujours sur la console. Évite d'envoyer un utilisateur dont le
 *  rôle n'a pas accès au tableau de bord sur une page masquée. */
export function landingPathFor(me: MeResponse | null | undefined): string {
  if (!me) return "/dashboard";
  if (me.is_platform_staff) return "/editeur";
  const hiddenNav = new Set(me.active_display_config?.hidden_nav ?? []);
  if (!hiddenNav.has("/dashboard")) return "/dashboard";
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (isNavItemVisible(me, item, hiddenNav)) return item.href;
    }
  }
  return "/profil";
}

/** Jeu de navigation selon le contexte : le personnel éditeur GTS ne voit QUE
 *  la console (Console, Abonnés, Licences, Plateforme) ; il accède aux espaces
 *  des abonnés via le ClinicSwitcher (handoff), pas par les menus cliniques.
 *  Les utilisateurs cliniques voient les menus métier. */
export function navGroupsFor(
  me:
    | {
        is_platform_staff?: boolean;
        active_display_config?: { features?: readonly string[] } | null;
      }
    | null
    | undefined,
): NavGroup[] {
  if (me?.is_platform_staff) return PLATFORM_NAV_GROUPS;
  // Organisation des menus de la clinique active : menus simplifiés (ADR
  // 0069), sinon nouvelle organisation (ADR 0061), sinon menus d'origine.
  const features = me?.active_display_config?.features;
  if (features?.includes("navigation_v3")) return NAV_V3_GROUPS;
  return features?.includes("navigation_v2") ? NAV_V2_GROUPS : NAV_GROUPS;
}

/** Bloc d'un menu : entrées consécutives de même sous-titre. */
export interface NavSection {
  /** Absent : entrées sans sous-titre (en tête de menu). */
  label?: string;
  items: NavItem[];
}

export interface VisibleNavGroup extends NavGroup {
  sections: NavSection[];
}

/** Regroupe les entrées consécutives de même sous-titre, dans l'ordre. */
export function navSections(items: NavItem[]): NavSection[] {
  const out: NavSection[] = [];
  for (const item of items) {
    const last = out[out.length - 1];
    if (last && last.label === item.section) last.items.push(item);
    else out.push({ label: item.section, items: [item] });
  }
  return out;
}

/** Menus visibles pour cet utilisateur : même règle pour la barre, le tiroir
 *  mobile et la palette. Une entrée masquée disparaît, un sous-titre sans
 *  entrée visible aussi, et un menu sans entrée visible ne s'affiche pas. */
export function visibleNavGroups(
  me: MeResponse | null | undefined,
  groups: NavGroup[] = navGroupsFor(me),
): VisibleNavGroup[] {
  const hiddenNav = new Set(me?.active_display_config?.hidden_nav ?? []);
  return groups
    .map((g) => {
      const items = g.items.flatMap((i) => {
        const resolved = resolvedNavItem(me, i, hiddenNav);
        return resolved ? [resolved] : [];
      });
      return { ...g, items, sections: navSections(items) };
    })
    .filter((g) => g.items.length > 0);
}

/** Entrée telle qu'affichée, ou `null` si masquée. Une entrée à onglets
 *  (ADR 0069) ne garde que ses onglets visibles et mène au premier ; elle
 *  disparaît quand aucun ne l'est. */
function resolvedNavItem(
  me: MeResponse | null | undefined,
  item: NavItem,
  hiddenNav: Set<string>,
): NavItem | null {
  if (!item.tabs) return isNavItemVisible(me, item, hiddenNav) ? swappedItem(me, item) : null;
  const tabs = item.tabs.filter((t) => isNavItemVisible(me, t, hiddenNav)).map((t) => swappedItem(me, t));
  const first = tabs[0];
  if (!first) return null;
  const swap = item.swapWhen;
  const label = swap && hasFeature(me ?? null, swap.feature) ? swap.label : item.label;
  return { ...item, label, href: first.href, tabs };
}

/** Entrée redirigée par son module (`swapWhen`), sinon telle quelle. */
export function swappedItem(me: MeResponse | null | undefined, item: NavItem): NavItem {
  const swap = item.swapWhen;
  if (!swap || !hasFeature(me ?? null, swap.feature)) return item;
  return { ...item, label: swap.label, href: swap.href, hiddenBy: navHideKey(item) };
}

/** Entrée de la palette « Aller à » : l'entrée de menu et son emplacement
 *  (« Stock · Approvisionnement »), dans l'ordre des menus. */
export interface NavDestination {
  item: NavItem;
  trail: string;
}

export function navDestinations(me: MeResponse | null | undefined): NavDestination[] {
  // Une entrée à onglets (ADR 0069) propose chacune de ses pages, sous son
  // nom habituel (« Familles » → « Clinique · Patients ») ; une page citée
  // deux fois n'apparaît qu'une fois.
  const seen = new Set<string>();
  return visibleNavGroups(me).flatMap((group) =>
    group.items.flatMap((item) => {
      const base = item.section ? `${group.label} · ${item.section}` : group.label;
      const pages = item.tabs
        ? item.tabs.map((tab) => ({
            item: tab,
            trail: tab.label === item.label ? base : `${group.label} · ${item.label}`,
          }))
        : [{ item, trail: base }];
      return pages.filter((p) => {
        if (seen.has(p.item.href)) return false;
        seen.add(p.item.href);
        return true;
      });
    }),
  );
}

/** L'entrée `href` correspond-elle à l'adresse courante ? Le chemin préfixe
 *  l'URL, et chaque paramètre de requête de l'entrée y figure à l'identique. */
function navHrefMatches(href: string, pathname: string, search: string): boolean {
  const [path = "", query = ""] = href.split("?", 2);
  if (pathname !== path && !pathname.startsWith(path + "/")) return false;
  if (!query) return true;
  const current = new URLSearchParams(search);
  for (const [key, value] of new URLSearchParams(query)) {
    if (current.get(key) !== value) return false;
  }
  return true;
}

/** Entrée active = la plus précise (href le plus long) qui correspond à
 *  l'adresse : un parent (`/editeur`, `/stock`) ne reste pas actif sur ses
 *  enfants ni sur ses liens profonds. Une entrée à onglets est active sur
 *  chacun de ses onglets ; à précision égale, une entrée directe l'emporte
 *  sur un onglet. `""` si aucune. */
export function activeNavHref(
  groups: NavGroup[],
  pathname: string,
  search = "",
): string {
  let active = "";
  let best = 0;
  for (const group of groups) {
    for (const item of group.items) {
      const candidates = [
        { href: item.href, score: item.href.length * 2 + 1 },
        ...(item.tabs ?? []).map((t) => ({ href: t.href, score: t.href.length * 2 })),
      ];
      for (const { href, score } of candidates) {
        if (score > best && navHrefMatches(href, pathname, search)) {
          best = score;
          active = item.href;
        }
      }
    }
  }
  return active;
}

/** Onglets à afficher en tête de page (ADR 0069) : ceux de l'entrée dont un
 *  onglet est exactement la page ouverte (pas ses fiches de détail). Rien
 *  sous deux pages distinctes : une page qui gère ses propres sections
 *  (espace Articles) n'a pas besoin de barre. */
export function navPageTabs(
  me: MeResponse | null | undefined,
  pathname: string,
  search = "",
): { tabs: NavItem[]; active: string } | null {
  for (const group of visibleNavGroups(me)) {
    for (const item of group.items) {
      const tabs = item.tabs ?? [];
      if (new Set(tabs.map((t) => hrefPath(t.href))).size < 2) continue;
      let active = "";
      for (const tab of tabs) {
        if (
          hrefPath(tab.href) === pathname &&
          tab.href.length > active.length &&
          navHrefMatches(tab.href, pathname, search)
        ) {
          active = tab.href;
        }
      }
      if (active) return { tabs, active };
    }
  }
  return null;
}

/** Pages « principales » (listes / racines de module) : une bascule de clinique
 *  peut y rester — les données se rechargent en place. Les pages de détail ou
 *  de création référencent une entité de l'ancienne clinique → retour accueil. */
const EXTRA_MAIN_PAGES = ["/notifications", "/profil", "/abonnement", "/articles"];

export function isMainPage(pathname: string): boolean {
  if (EXTRA_MAIN_PAGES.includes(pathname)) return true;
  return [...NAV_GROUPS, ...NAV_V2_GROUPS, ...NAV_V3_GROUPS, ...PLATFORM_NAV_GROUPS].some((g) =>
    g.items.some((i) => i.href === pathname || (i.tabs ?? []).some((t) => t.href === pathname)),
  );
}

export const APP_LOGO_TEXT = "MADA";
export const APP_SUBTITLE = "Médical · Accueil · Dossiers · Administration";
